import React, { Component } from 'react';

/**
 * Convierte hora 24h ("HH:MM") a formato display según el formato configurado.
 * @param {string} time24 - Hora en formato 24h, ej: "14:30"
 * @param {string} format - "12h" o "24h" (null = 24h)
 * @returns {string} - Hora formateada, ej: "02:30 PM" o "14:30"
 */
export function formatTimeForDisplay(time24, format) {
    if (!time24 || typeof time24 !== 'string') return time24 || '';
    const parts = time24.split(':');
    if (parts.length < 2) return time24;

    const h = parseInt(parts[0], 10);
    const m = parts[1];
    const fmt = format || '12h';

    if (fmt === '12h') {
        const period = h >= 12 ? 'PM' : 'AM';
        const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${String(h12).padStart(2, '0')}:${m} ${period}`;
    }
    return time24;
}

/**
 * Convierte hora 12h a 24h.
 * @param {number} hour12 - Hora 1-12
 * @param {string} minutes - Minutos "00"-"59"
 * @param {string} period - "AM" o "PM"
 * @returns {string} - "HH:MM" en 24h
 */
function to24h(hour12, minutes, period) {
    let h = hour12;
    if (period === 'AM') {
        h = hour12 === 12 ? 0 : hour12;
    } else {
        h = hour12 === 12 ? 12 : hour12 + 12;
    }
    return `${String(h).padStart(2, '0')}:${minutes}`;
}

/**
 * Parsea un valor 24h a componentes 12h.
 * @param {string} value24 - "HH:MM"
 * @returns {{ hour12: number, minutes: string, period: string }}
 */
function parse24hTo12h(value24) {
    if (!value24 || typeof value24 !== 'string') return { hour12: 12, minutes: '00', period: 'AM' };
    const parts = value24.split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parts[1] || '00';
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return { hour12, minutes: m, period };
}

class TimeSelectField extends Component {
    constructor(props) {
        super(props);
        this.state = this.getStateFromValue(props.value);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            this.setState(this.getStateFromValue(this.props.value));
        }
    }

    getStateFromValue = (value) => {
        const format = this.props.format || '12h';
        const val = (Array.isArray(value) ? value[0] : value) || '';

        if (format === '12h') {
            const { hour12, minutes, period } = parse24hTo12h(val);
            return { hour: String(hour12), minutes, period };
        } else {
            const parts = val.split(':');
            return { hour: parts[0] || '00', minutes: parts[1] || '00', period: 'AM' };
        }
    }

    emitChange = (hour, minutes, period) => {
        const format = this.props.format || '12h';
        let value24;

        if (format === '12h') {
            value24 = to24h(parseInt(hour, 10) || 12, minutes, period);
        } else {
            value24 = `${hour}:${minutes}`;
        }

        const syntheticEvent = {
            target: {
                value: value24,
                name: this.props.name || 'answer_hour',
                dataset: { ...this.props.dataset }
            }
        };

        if (this.props.onChange) {
            this.props.onChange(syntheticEvent);
        }
    }

    handleHourChange = (e) => {
        const hour = e.target.value;
        this.setState({ hour }, () => {
            this.emitChange(hour, this.state.minutes, this.state.period);
        });
    }

    handleMinuteChange = (e) => {
        const minutes = e.target.value;
        this.setState({ minutes }, () => {
            this.emitChange(this.state.hour, minutes, this.state.period);
        });
    }

    handlePeriodChange = (e) => {
        const period = e.target.value;
        this.setState({ period }, () => {
            this.emitChange(this.state.hour, this.state.minutes, period);
        });
    }

    render() {
        const format = this.props.format || '12h';
        const disabled = this.props.disabled || false;
        const style = this.props.style || {};
        const is12h = format === '12h';

        const hours = is12h
            ? Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))
            : Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

        const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

        return (
            <div style={{ ...style, display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '4px' }}>
                <select
                    className="form-control"
                    value={is12h ? String(parseInt(this.state.hour, 10)).padStart(2, '0') : this.state.hour}
                    onChange={this.handleHourChange}
                    disabled={disabled}
                    style={{ width: 'auto', minWidth: '60px' }}
                >
                    {hours.map(h => (
                        <option key={h} value={h}>{h}</option>
                    ))}
                </select>
                <span style={{ fontWeight: 'bold' }}>:</span>
                <select
                    className="form-control"
                    value={this.state.minutes}
                    onChange={this.handleMinuteChange}
                    disabled={disabled}
                    style={{ width: 'auto', minWidth: '60px' }}
                >
                    {minuteOptions.map(m => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>
                {is12h && (
                    <select
                        className="form-control"
                        value={this.state.period}
                        onChange={this.handlePeriodChange}
                        disabled={disabled}
                        style={{ width: 'auto', minWidth: '65px' }}
                    >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                    </select>
                )}
            </div>
        );
    }
}

export default TimeSelectField;
